import { Add, Delete, Info } from '@mui/icons-material';
import {
    Box,
    Checkbox,
    CircularProgress,
    FormControl,
    IconButton,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    activeTableSortLabel,
    MuiDisabledSelect40,
    MuiDisablesTextFieldWithFocus40,
    MuiDisablesTextFieldWithFocus4016,
    MuiSelectWithFocus40,
    MuiTextFieldWithFocus40,
    MuiTextFieldWithFocus4016,
} from '../../../../config/MaterialUIConstants';
import { formatNumberDecimaWithoutRoundoff, HtmlTooltip } from '../../../../utility/helpers/commonFunction';
import { formatCurrency } from '../../../../utility/helpers/displayHelper';
import { PAYMENT_METHODS } from '../../GenrateInvoice/RecordPayementDialog';
import { USER_TIMEZONE } from '../../../../utility/Constant';
import dayjs from 'dayjs';
import moment from 'moment';

// PaymentRow component - Updated with deferred validation logic
const PaymentRow = React.memo(
    ({
        invoice,
        onlyOneCustomerSelected,
        invoicePaymentStatus,
        isSelected,
        selectedRows,
        setSelectedRows,
        updatePayment,
        addPayment,
        deletePayment,
        shouldShowValidationErrors, // New prop to control when to show validation errors
    }) => {
        const renderPaymentMethodOption = (method) => (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                {method.logo && <img src={method.logo} alt={method.label} style={{ height: '20px', width: 'auto' }} />}
                <Typography sx={{ fontSize: '16px' }}>{method.label}</Typography>
            </Box>
        );

        const payments = invoice.payments || [];

        // Calculate validation states
        const getTotalPaymentAmount = () => {
            return payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0);
        };

        const remainingAmount = parseFloat(invoice.remainingPayment) || 0;
        const invoiceTotalAmount = parseFloat(invoice.Total) || 0;
        const totalPaymentAmount = formatNumberDecimaWithoutRoundoff(getTotalPaymentAmount());

        // Validation logic - now considering shouldShowValidationErrors
        const isOverRemaining = totalPaymentAmount > invoiceTotalAmount;
        const isZeroPayment = isSelected && shouldShowValidationErrors && totalPaymentAmount === 0;

        // Get validation style for individual payment input
        const getPaymentInputValidationStyle = (paymentIndex, array) => {
            if (!isSelected) return {};

            const currentPayment = payments[paymentIndex];
            const currentAmount = parseFloat(currentPayment?.amount) || 0;

            // Check if this specific payment is invalid
            const isThisPaymentZero = shouldShowValidationErrors && currentAmount === 0 && currentPayment?.paymentMethod;
            const isThisPaymentOver = totalPaymentAmount > invoiceTotalAmount;

            if (isThisPaymentZero) {
                return {};
            } else if (isThisPaymentOver && paymentIndex === array.length - 1) {
                return {
                    '& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FFAE00!important',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FFAE00!important',
                    },
                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notched0Outline': {
                        borderColor: '#FFAE00!important',
                    },
                    '& .MuiOutlinedInput-root.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#FFAE00!important',
                    },
                };
            }
            return {};
        };

        // Handle payment amount focus - auto-fill remaining amount
        const handlePaymentFocus = (paymentIndex) => {
            const payment = payments[paymentIndex];
            if (!payment.amount || payment.amount === '') {
                // Calculate how much is left to pay
                const currentPaymentSum = payments.reduce((sum, p, index) => {
                    if (index === paymentIndex) return sum;
                    return sum + (parseFloat(p.amount) || 0);
                }, 0);
                let suggestedAmount = (Math.round(invoiceTotalAmount * 100) - Math.round(currentPaymentSum * 100)) / 100;
                console.log('🚀 ~ handlePaymentFocus ~ suggestedAmount:', suggestedAmount);

                suggestedAmount = formatNumberDecimaWithoutRoundoff(suggestedAmount);
                console.log('🚀 ~ handlePaymentFocus ~ suggestedAmount:', suggestedAmount);

                if (suggestedAmount > 0 && suggestedAmount > 0) {
                    updatePayment(invoice._id, paymentIndex, {
                        amount: suggestedAmount.toString(),
                    });
                }
            }
        };

        return (
            <TableRow
                selected={isSelected}
                sx={{
                    '& .MuiTableCell-root': {
                        verticalAlign: 'center',
                        py: 1,
                    },
                    transition: 'all 0.3s ease-in-out',
                }}
            >
                {/* Checkbox */}
                <TableCell padding="checkbox" sx={{ width: '48px' }}>
                    <Checkbox
                        checked={isSelected}
                        onChange={() => setSelectedRows(isSelected ? selectedRows.filter((id) => id !== invoice._id) : [...selectedRows, invoice._id])}
                    />
                </TableCell>

                {/* Invoice Number */}
                <TableCell sx={{ width: '115px', fontSize: '16px', color: invoicePaymentStatus === 'fullyPaid' && '#2E7D32' }}>
                    {invoice.invoiceNumber}
                </TableCell>

                {/* Date */}
                <TableCell sx={{ width: '115px', fontSize: '16px' }}>
                    {/* {new Date(invoice.createdAt).toLocaleDateString()} */}
                    {invoice?.invoiceDate
                        ? dayjs(invoice?.invoiceDate).tz(USER_TIMEZONE).format('MMM D, YYYY')
                        : moment(invoice && invoice?.createdAt).format('MMM D, YYYY')}
                </TableCell>

                {/* Customer */}
                {!onlyOneCustomerSelected && <TableCell sx={{ width: '145px', fontSize: '16px' }}>{invoice.customer?.fullName || ''}</TableCell>}

                {/* Company */}
                {!onlyOneCustomerSelected && <TableCell sx={{ width: '145px', fontSize: '16px' }}>{invoice.customer?.company || ''}</TableCell>}

                {/* Total */}
                <TableCell align="right" sx={{ width: '100px', fontSize: '16px', color: invoicePaymentStatus === 'fullyPaid' && '#2E7D32' }}>
                    {formatCurrency(invoice.Total)}
                </TableCell>

                {/* Remaining */}
                <TableCell align="right" sx={{ width: '100px', fontSize: '16px' }}>
                    {formatCurrency(invoice.remainingPayment)}
                </TableCell>

                {/* Payment Type */}
                <TableCell sx={{ width: '140px' }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {payments.map((payment, i) => {
                            // Only show method selection error if shouldShowValidationErrors is true
                            const hasMethodError = shouldShowValidationErrors && parseFloat(payment.amount || 0) > 0 && !payment.paymentMethod;

                            return (
                                <FormControl key={i} size="small" sx={{ minWidth: 120 }}>
                                    <Select
                                        value={payment.paymentMethod}
                                        onChange={(e) => updatePayment(invoice._id, i, { paymentMethod: e.target.value })}
                                        disabled={!isSelected}
                                        sx={isSelected ? { ...MuiSelectWithFocus40, background: '#fff' } : { ...MuiDisabledSelect40 }}
                                        MenuProps={{ PaperProps: { style: { maxHeight: '190px' } } }}
                                        error={hasMethodError}
                                    >
                                        {PAYMENT_METHODS.map((option) => (
                                            <MenuItem key={option.key} value={option.key}>
                                                {renderPaymentMethodOption(option)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            );
                        })}
                    </Box>
                </TableCell>

                {/* Payment Amount */}
                <TableCell sx={{ width: '200px' }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {payments.map((payment, i) => {
                            const currentAmount = parseFloat(payment.amount) || 0;
                            // Only show zero error if shouldShowValidationErrors is true
                            const isZero = isSelected && shouldShowValidationErrors && (payment.amount === '' || payment.amount == '0');
                            const isOver = isSelected && totalPaymentAmount > invoiceTotalAmount && i === payments.length - 1;

                            const getTooltipMessage = () => {
                                if (isOver) return 'Payment is more than the remaining amount';
                                if (isZero) return 'Amount must be greater than zero';
                                return '';
                            };

                            const hasError = isZero || isOver;

                            const inputStyles = isSelected
                                ? { ...MuiTextFieldWithFocus4016, background: '#fff', ...getPaymentInputValidationStyle(i, payments) }
                                : { ...MuiDisablesTextFieldWithFocus4016, background: '#fff' };

                            const handleChange = (event) => {
                                let inputValue = event.target.value;

                                // Remove leading zero if followed by a valid digit/letter
                                if (inputValue.length > 1 && inputValue.startsWith('0') && /[1-9a-zA-Z]/.test(inputValue[1])) {
                                    inputValue = inputValue.slice(1);
                                }

                                // Allow empty or valid decimal (max 2 digits after decimal)
                                if (inputValue === '' || /^\d*(\.\d{0,2})?$/.test(inputValue)) {
                                    updatePayment(invoice._id, i, { amount: inputValue });
                                }
                            };

                            const handleFocus = (event) => {
                                handlePaymentFocus(i);
                                const input = event.target;
                                setTimeout(() => {
                                    input.setSelectionRange(input.value.length, input.value.length);
                                }, 0);
                            };

                            return (
                                <Box key={i} display="flex" alignItems="center" gap={0.5} sx={{ flexWrap: 'nowrap' }}>
                                    <TextField
                                        value={payment.amount || ''}
                                        disabled={!isSelected}
                                        error={hasError}
                                        onFocus={handleFocus}
                                        onChange={handleChange}
                                        InputProps={{
                                            startAdornment: (
                                                <p
                                                    style={{
                                                        paddingLeft: 0,
                                                        color: isSelected ? 'rgba(0, 0, 0, 0.87)' : 'rgba(0, 0, 0, 0.38)',
                                                        fontSize: '16px',
                                                    }}
                                                >
                                                    $
                                                </p>
                                            ),
                                            endAdornment: isSelected && hasError && (
                                                <HtmlTooltip title={getTooltipMessage()} placement="top" arrow>
                                                    <Info
                                                        sx={{
                                                            fontSize: '24px',
                                                            color: isZero ? '#D32F2F' : isOver ? '#FFAE00' : 'transparent',
                                                            display: !isZero && !isOver && 'none',
                                                        }}
                                                    />
                                                </HtmlTooltip>
                                            ),
                                        }}
                                        style={{ borderRadius: '8px', fontSize: '16px', marginRight: '8px' }}
                                        sx={inputStyles}
                                    />

                                    {payments.length > 1 && (
                                        <IconButton
                                            size="small"
                                            onClick={() => deletePayment(invoice._id, i)}
                                            disabled={!isSelected}
                                            sx={{ opacity: isSelected ? 1 : 0.4, padding: 0 }}
                                        >
                                            <Delete sx={{ color: '#D32F2F' }} />
                                        </IconButton>
                                    )}

                                    <IconButton
                                        size="small"
                                        onClick={() => addPayment(invoice._id)}
                                        disabled={!isSelected}
                                        sx={{ opacity: isSelected ? 1 : 0.4, padding: 0 }}
                                    >
                                        <Add sx={{ color: '#006CDD' }} />
                                    </IconButton>
                                </Box>
                            );
                        })}
                    </Box>
                </TableCell>

                {/* Payment Reference */}
                <TableCell sx={{ width: '180px' }}>
                    <Box display="flex" flexDirection="column" gap={1}>
                        {payments.map((payment, i) => (
                            <TextField
                                size="small"
                                key={i}
                                value={payment.paymentReference || ''}
                                onChange={(e) => updatePayment(invoice._id, i, { paymentReference: e.target.value })}
                                disabled={!isSelected}
                                style={{ borderRadius: '8px', fontSize: '16px!important' }}
                                sx={
                                    isSelected
                                        ? {
                                              fontSize: '16px!important',
                                              ...MuiTextFieldWithFocus40,
                                              background: '#fff',
                                              '& .MuiInputBase-root': {
                                                  fontSize: '16px!important',
                                              },
                                          }
                                        : {
                                              fontSize: '16px!important',
                                              ...MuiDisablesTextFieldWithFocus40,
                                              background: '#F1F1F1',
                                              '& .MuiInputBase-root': {
                                                  fontSize: '16px!important',
                                              },
                                          }
                                }
                            />
                        ))}
                    </Box>
                </TableCell>
            </TableRow>
        );
    }
);

// Add display name for ESLint
PaymentRow.displayName = 'PaymentRow';

const RecordPaymentListTable = ({
    onlyOneCustomerSelected,
    recordPaymentList,
    recordPaymentPagaintion,
    recordPaymentParams,
    setRecordPaymentParams,
    selectedRows,
    setSelectedRows,
    getMultipleRecordPayments,
    loadingMore,
    updatePayment,
    addPayment,
    deletePayment,
}) => {
    const containerRef = useRef(null);
    const [visibleRange, setVisibleRange] = useState({ start: 0, end: 20 });
    const [validationTrigger, setValidationTrigger] = useState(new Set());
    const BUFFER_SIZE = 5;

    // Function to determine which invoices should show validation errors
    const shouldShowValidationErrors = useCallback(
        (invoiceId) => {
            return validationTrigger.has(invoiceId);
        },
        [validationTrigger]
    );

    // Enhanced setSelectedRows with validation trigger logic
    const handleSetSelectedRows = useCallback(
        (newSelectedRows) => {
            const currentSelected = new Set(selectedRows);
            const newSelected = new Set(Array.isArray(newSelectedRows) ? newSelectedRows : []);

            // Find previously selected rows that are still selected
            const stillSelected = selectedRows.filter((id) => newSelected.has(id));

            // If user is selecting new rows while having existing selections,
            // trigger validation for the previously selected rows
            if (stillSelected.length > 0 && newSelected.size > currentSelected.size) {
                setValidationTrigger((prev) => new Set([...prev, ...stillSelected]));
            }

            // If user is deselecting a row, remove it from validation trigger
            const deselectedRows = selectedRows.filter((id) => !newSelected.has(id));
            if (deselectedRows.length > 0) {
                setValidationTrigger((prev) => {
                    const newTrigger = new Set(prev);
                    deselectedRows.forEach((id) => newTrigger.delete(id));
                    return newTrigger;
                });
            }

            setSelectedRows(newSelectedRows);
        },
        [selectedRows, setSelectedRows]
    );

    // Enhanced addPayment with validation trigger
    const handleAddPayment = useCallback(
        (invoiceId) => {
            // Trigger validation for all currently selected rows when adding payment
            setValidationTrigger((prev) => new Set([...prev, ...selectedRows]));
            addPayment(invoiceId);
        },
        [addPayment, selectedRows]
    );

    // Handle sorting functionality
    const handleSort = useCallback(
        (sortByField) => {
            const isCurrentSort = recordPaymentParams.sortBy === sortByField;
            const newSortType = isCurrentSort && recordPaymentParams.sortType === 'ASC' ? 'DSC' : 'ASC';

            setRecordPaymentParams({
                ...recordPaymentParams,
                sortBy: sortByField,
                sortType: newSortType,
            });
        },
        [recordPaymentParams.sortBy, recordPaymentParams.sortType, setRecordPaymentParams]
    );

    // Virtual scrolling with intersection observer
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const handleScroll = () => {
            const scrollTop = container.scrollTop;
            const containerHeight = container.clientHeight;
            const scrollHeight = container.scrollHeight;

            // Calculate visible range based on scroll position
            const avgRowHeight = 80;
            const start = Math.max(0, Math.floor(scrollTop / avgRowHeight) - BUFFER_SIZE);
            const end = Math.min(recordPaymentList.length, Math.ceil((scrollTop + containerHeight) / avgRowHeight) + BUFFER_SIZE);

            setVisibleRange({ start, end });

            // Load more data when near bottom
            if (scrollTop + containerHeight >= scrollHeight - 100 && !loadingMore && recordPaymentList.length < recordPaymentPagaintion.totalCount) {
                getMultipleRecordPayments(recordPaymentPagaintion.currentPage + 1, true);
            }
        };

        container.addEventListener('scroll', handleScroll);
        handleScroll();

        return () => container.removeEventListener('scroll', handleScroll);
    }, [recordPaymentList.length, loadingMore, recordPaymentPagaintion.totalCount, recordPaymentPagaintion.currentPage, getMultipleRecordPayments]);

    const visibleRows = useMemo(() => {
        return recordPaymentList.slice(visibleRange.start, visibleRange.end);
    }, [recordPaymentList, visibleRange]);

    // Reset validation triggers when selectedRows changes to empty
    useEffect(() => {
        if (selectedRows.length === 0) {
            setValidationTrigger(new Set());
        }
    }, [selectedRows]);

    return (
        <TableContainer
            ref={containerRef}
            sx={{
                mt: 2,
                height: '56vh',
                borderRadius: '4px',
                backgroundColor: '#fff',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.12), 0 1px 1px 0 rgba(0, 0, 0, 0.14), 0 2px 1px -1px rgba(0, 0, 0, 0.20)',
                overflow: 'auto',
                width: '100%',
            }}
        >
            <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%' }}>
                <TableHead>
                    <TableRow>
                        <TableCell padding="checkbox" sx={{ width: '48px', fontSize: '16px' }}></TableCell>
                        <TableCell sx={{ width: '120px', fontSize: '16px' }}>Invoice #</TableCell>

                        {/* Sortable Date Column */}
                        <TableCell sx={{ width: '120px', fontSize: '16px' }}>
                            <TableSortLabel
                                active={true}
                                direction={recordPaymentParams.sortBy === 'createdAt' ? recordPaymentParams.sortType.toLowerCase() : 'asc'}
                                onClick={() => handleSort('createdAt')}
                                sx={recordPaymentParams.sortBy === 'createdAt' && activeTableSortLabel}
                            >
                                Date
                            </TableSortLabel>
                        </TableCell>

                        {/* Sortable Customer Column */}
                        {!onlyOneCustomerSelected && (
                            <TableCell sx={{ width: '150px', fontSize: '16px' }}>
                                <TableSortLabel
                                    active={true}
                                    direction={recordPaymentParams.sortBy === 'customer.fullName' ? recordPaymentParams.sortType.toLowerCase() : 'dsc'}
                                    onClick={() => handleSort('customer.fullName')}
                                    sx={recordPaymentParams.sortBy === 'customer.fullName' && activeTableSortLabel}
                                >
                                    Customer
                                </TableSortLabel>
                            </TableCell>
                        )}
                        {!onlyOneCustomerSelected && <TableCell sx={{ width: '150px', fontSize: '16px' }}>Company</TableCell>}

                        <TableCell align="right" sx={{ width: '100px', fontSize: '16px' }}>
                            Total
                        </TableCell>
                        <TableCell align="right" sx={{ width: '100px', fontSize: '16px' }}>
                            Remaining
                        </TableCell>
                        <TableCell sx={{ width: '180px', fontSize: '16px' }}>Type</TableCell>
                        <TableCell sx={{ width: '180px', fontSize: '16px' }}>Payment</TableCell>
                        <TableCell sx={{ width: '180px', fontSize: '16px' }}>Payment Reference</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* Spacer for rows before visible range */}
                    {visibleRange.start > 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={onlyOneCustomerSelected ? 8 : 10}
                                sx={{
                                    height: visibleRange.start * 80,
                                    p: 0,
                                    border: 'none',
                                }}
                            />
                        </TableRow>
                    )}
                    {/* Show Empty State if no records */}
                    {recordPaymentList.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={onlyOneCustomerSelected ? 8 : 10}
                                align="center"
                                sx={{
                                    height: '50vh',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: 'rgba(0,0,0,0.6)',
                                }}
                            >
                                No records found
                            </TableCell>
                        </TableRow>
                    )}

                    {visibleRows.map((invoice) => (
                        <PaymentRow
                            key={invoice._id}
                            onlyOneCustomerSelected={onlyOneCustomerSelected}
                            invoice={invoice}
                            invoicePaymentStatus={invoice.invoicePaymentStatus}
                            isSelected={selectedRows.includes(invoice._id)}
                            selectedRows={selectedRows}
                            setSelectedRows={handleSetSelectedRows}
                            updatePayment={updatePayment}
                            addPayment={handleAddPayment}
                            deletePayment={deletePayment}
                            shouldShowValidationErrors={shouldShowValidationErrors(invoice._id)}
                        />
                    ))}

                    {/* Spacer for rows after visible range */}
                    {visibleRange.end < recordPaymentList.length && (
                        <TableRow>
                            <TableCell
                                colSpan={onlyOneCustomerSelected ? 8 : 10}
                                sx={{
                                    height: (recordPaymentList.length - visibleRange.end) * 80,
                                    p: 0,
                                    border: 'none',
                                }}
                            />
                        </TableRow>
                    )}
                </TableBody>
            </Table>

            {loadingMore && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        textAlign: 'center',
                        p: 2,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        borderTop: '1px solid #e0e0e0',
                    }}
                >
                    <CircularProgress size={20} />
                </Box>
            )}
        </TableContainer>
    );
};
export default RecordPaymentListTable;
